import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, RefreshCw, Inbox, ArrowUpRight, Phone, Mail } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { apiClient, formatErr, STATUS_LABELS, STATUS_COLORS } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["", "nuovo", "in_lavorazione", "bonifico_inviato", "spedito", "chiuso"];

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ totale: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", q: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.q.trim()) params.q = filter.q.trim();
      const [qRes, sRes] = await Promise.all([
        apiClient.get("/admin/quotes", { params }),
        apiClient.get("/admin/quotes/stats"),
      ]);
      setQuotes(qRes.data);
      setStats(sRes.data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const formatDate = (iso) =>
    new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <AdminLayout
      title="Richieste Preventivo"
      subtitle="Dashboard"
      actions={
        <button
          onClick={load}
          data-testid="admin-refresh-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-300 hover:border-zinc-900 text-zinc-700 hover:text-zinc-900 text-xs uppercase tracking-widest font-mono transition-colors bg-white"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          Aggiorna
        </button>
      }
    >
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-200 mb-8 border border-zinc-200">
        <div className="bg-white p-5">
          <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-1">
            Totale
          </div>
          <div className="font-heading font-black text-3xl text-zinc-900">
            {stats.totale ?? 0}
          </div>
        </div>
        {Object.entries(STATUS_LABELS).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter({ ...filter, status: filter.status === k ? "" : k })}
            data-testid={`stat-${k}`}
            className={`bg-white hover:bg-zinc-50 p-5 text-left transition-colors ${
              filter.status === k ? "ring-1 ring-[#E63946] ring-inset" : ""
            }`}
          >
            <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-400 mb-1">
              {label}
            </div>
            <div className="font-heading font-black text-3xl text-zinc-900">
              {stats[k] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            data-testid="admin-search"
            placeholder="Cerca per nome, telefono, codice, marca..."
            className="w-full bg-white border border-zinc-300 pl-10 pr-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-[#E63946] transition-colors"
          />
        </div>
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          data-testid="admin-filter-status"
          className="bg-white border border-zinc-300 px-4 py-3 text-sm text-zinc-900 focus:outline-none focus:border-[#E63946] transition-colors"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s ? STATUS_LABELS[s] : "Tutti gli stati"}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="admin-quotes-table">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-left text-[10px] uppercase tracking-widest font-mono text-zinc-500">
              <th className="px-5 py-4">Data</th>
              <th className="px-5 py-4">Cliente</th>
              <th className="px-5 py-4">Contatti</th>
              <th className="px-5 py-4">Ricambio</th>
              <th className="px-5 py-4">Stato</th>
              <th className="px-5 py-4 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {!loading && quotes.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center text-zinc-400">
                  <Inbox size={32} className="mx-auto mb-3 opacity-50" />
                  Nessuna richiesta trovata.
                </td>
              </tr>
            )}
            {quotes.map((q) => (
              <tr
                key={q.id}
                data-testid={`quote-row-${q.id}`}
                className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors"
              >
                <td className="px-5 py-4 font-mono text-xs text-zinc-500 whitespace-nowrap">
                  {formatDate(q.created_at)}
                </td>
                <td className="px-5 py-4">
                  <div className="font-bold text-zinc-900">{q.nome}</div>
                  <div className="text-xs text-zinc-500">{q.marca_modello || "—"}</div>
                </td>
                <td className="px-5 py-4 text-xs">
                  <div className="flex items-center gap-1.5 text-zinc-700">
                    <Phone size={11} className="text-[#E63946]" />
                    {q.telefono}
                  </div>
                  {q.email && (
                    <div className="flex items-center gap-1.5 text-zinc-500 mt-1">
                      <Mail size={11} />
                      {q.email}
                    </div>
                  )}
                </td>
                <td className="px-5 py-4">
                  <div className="text-zinc-900 font-medium">{q.tipo_ricambio}</div>
                  <div className="text-xs text-zinc-500 font-mono">{q.codice_motore}</div>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-widest font-mono font-bold ${
                      STATUS_COLORS[q.status] || STATUS_COLORS.nuovo
                    }`}
                  >
                    {STATUS_LABELS[q.status] || q.status}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <Link
                    to={`/admin/q/${q.id}`}
                    data-testid={`quote-open-${q.id}`}
                    className="inline-flex w-8 h-8 items-center justify-center bg-zinc-900 text-white hover:bg-[#E63946] transition-colors"
                  >
                    <ArrowUpRight size={14} strokeWidth={2.4} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
